import type {ElementNode, LexicalCommand, LexicalNode, NodeKey} from 'lexical'

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext'
import {$insertNodeToNearestRoot, mergeRegister} from '@lexical/utils'
import {
    $createParagraphNode,
    $getNodeByKey,
    $getSelection,
    $isElementNode,
    $isRangeSelection,
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_LOW,
    createCommand,
    KEY_BACKSPACE_COMMAND,
    KEY_DELETE_COMMAND,
} from 'lexical'
import {useEffect} from 'react'
import {$createLayoutContainerNode, $isLayoutContainerNode, LayoutContainerNode} from "./node/container-node";
import {$createLayoutItemNode, $isLayoutItemNode, LayoutItemNode} from "./node/item-node";

export const INSERT_LAYOUT_COMMAND: LexicalCommand<string> =
    createCommand<string>();

export const UPDATE_LAYOUT_COMMAND: LexicalCommand<{
    template: string;
    nodeKey: NodeKey;
}> = createCommand<{template: string; nodeKey: NodeKey}>();

export function LayoutPlugin(): null {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        if (!editor.hasNodes([LayoutContainerNode, LayoutItemNode])) {
            throw new Error(
                'LayoutPlugin: LayoutContainerNode, or LayoutItemNode not registered on editor',
            );
        }

        return mergeRegister(
            editor.registerCommand(
                INSERT_LAYOUT_COMMAND,
                (template) => {
                    editor.update(() => {
                        const container = $createLayoutContainerNode(template);
                        const itemsCount = getItemsCountFromTemplate(template);

                        for (let i = 0; i < itemsCount; i++) {
                            container.append(
                                $createLayoutItemNode().append($createParagraphNode()),
                            );
                        }

                        $insertNodeToNearestRoot(container);
                        container.selectStart();
                    });

                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
            editor.registerCommand(
                KEY_BACKSPACE_COMMAND,
                (event) => {
                    return $removeEmptyLayoutItemOnDelete(event);
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                KEY_DELETE_COMMAND,
                (event) => {
                    return $removeEmptyLayoutItemOnDelete(event);
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                UPDATE_LAYOUT_COMMAND,
                ({template, nodeKey}) => {
                    editor.update(() => {
                        const container = $getNodeByKey<LexicalNode>(nodeKey);

                        if (!$isLayoutContainerNode(container)) {
                            return;
                        }

                        const itemsCount = getItemsCountFromTemplate(template);
                        const prevItemsCount = getItemsCountFromTemplate(
                            container.getTemplateColumns(),
                        );

                        // Add or remove extra columns if new template does not match existing one
                        if (itemsCount > prevItemsCount) {
                            for (let i = prevItemsCount; i < itemsCount; i++) {
                                container.append(
                                    $createLayoutItemNode().append($createParagraphNode()),
                                );
                            }
                        } else if (itemsCount < prevItemsCount) {
                            for (let i = prevItemsCount; i < itemsCount; i++) {
                                const layoutItem = container.getChildAtIndex<LexicalNode>(i);

                                if ($isLayoutItemNode(layoutItem)) {
                                    for (const child of layoutItem.getChildren<LexicalNode>()) {
                                        container.insertAfter(child);
                                    }
                                }
                            }
                        }

                        container.setTemplateColumns(template);
                    });

                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
            // Structure enforcing transformers for each node type. In case nesting structure is not
            // "Container > Item" it'll unwrap nodes and convert it back
            // to regular content.
            editor.registerNodeTransform(LayoutItemNode, (node) => {
                const parent = node.getParent<ElementNode>();
                if (!$isLayoutContainerNode(parent)) {
                    const children = node.getChildren<LexicalNode>();
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),
            editor.registerNodeTransform(LayoutContainerNode, (node) => {
                const children = node.getChildren<LexicalNode>();
                if (!children.every($isLayoutItemNode)) {
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),
        );
    }, [editor]);

    return null
}

function getItemsCountFromTemplate(template: string): number {
    return template.trim().split(/\s+/).length;
}

function $findLayoutItem(node: LexicalNode | null): LexicalNode | null {
    let current = node;
    while (current !== null) {
        if ($isLayoutItemNode(current)) {
            return current;
        }
        current = current.getParent();
    }
    return null;
}

function $isLayoutItemEmpty(item: LexicalNode): boolean {
    if (!$isElementNode(item)) {
        return false;
    }
    const children = item.getChildren<LexicalNode>();
    if (children.length === 0) {
        return true;
    }
    return children.every((child) => $isElementNode(child) && child.getChildrenSize() === 0);
}

function $removeEmptyLayoutItemOnDelete(event: KeyboardEvent): boolean {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
        return false;
    }

    const layoutItem = $findLayoutItem(selection.anchor.getNode());
    if (layoutItem === null || !$isLayoutItemEmpty(layoutItem)) {
        return false;
    }

    const container = layoutItem.getParent<ElementNode>();
    if (!$isLayoutContainerNode(container)) {
        return false;
    }

    const previousItem      = layoutItem.getPreviousSibling<LexicalNode>();
    const nextItem          = layoutItem.getNextSibling<LexicalNode>();
    const containerSibling  = container.getPreviousSibling<LexicalNode>() ?? container.getNextSibling<LexicalNode>();

    layoutItem.remove();

    if ($isLayoutItemNode(previousItem)) {
        previousItem.selectEnd();
    } else if ($isLayoutItemNode(nextItem)) {
        nextItem.selectStart();
    } else if ($isElementNode(containerSibling)) {
        containerSibling.selectEnd();
    }

    event.preventDefault();
    return true;
}
